import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ParkingCandidate, ParkingRecovery } from 'services/ParkingAgentService';

const METHOD_LABEL: Record<ParkingCandidate['method'], string> = {
  closed_lines: '닫힌 경계',
  triangle_pair: '삼각형 병합',
  notched_lines: '경계 기호 보정',
};
// 맵 에디터 장애인 주차 색(LayerSelectionModal getTypeColor)과 같다.
const DISABLED_STROKE = '#e11d48';

const PILLAR_STROKE = '#7c3aed';

const disabledLabel = (candidate: ParkingCandidate): string =>
  candidate.parking_type === 'p-parking-disable'
    ? ' · 장애인'
    : candidate.parking_type === 's-pillar'
      ? ' · 기둥'
      : '';

interface RecoveryPreviewProps {
  result: ParkingRecovery;
  selected: Set<string>;
  onToggle: (id: string) => void;
}

const RecoveryPreview: React.FC<RecoveryPreviewProps> = ({
  result,
  selected,
  onToggle,
}) => {
  const [zoom, setZoom] = useState(1);
  const bounds = useMemo(() => {
    const points = result.candidates.flatMap((c) => c.vertices);
    if (!points.length) return { x: 0, y: 0, width: 100, height: 100 };
    const xs = points.map((p) => p[0]),
      ys = points.map((p) => -p[1]);
    const x = Math.min(...xs) - 2,
      y = Math.min(...ys) - 2;
    return {
      x,
      y,
      width: Math.max(10, Math.max(...xs) - x + 2),
      height: Math.max(10, Math.max(...ys) - y + 2),
    };
  }, [result]);
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button
          onClick={() => setZoom((z) => Math.min(8, z * 2))}
          disabled={zoom >= 8}
          sx={{ minHeight: 44 }}
        >
          확대
        </Button>
        <Button
          onClick={() => setZoom((z) => Math.max(1, z / 2))}
          disabled={zoom <= 1}
          sx={{ minHeight: 44 }}
        >
          축소
        </Button>
        <Button onClick={() => setZoom(1)} sx={{ minHeight: 44 }}>
          전체 보기
        </Button>
        <Typography variant="body2">{Math.round(zoom * 100)}%</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        초록색은 선택한 후보, 빨간 테두리는 빗금 통로가 붙어 장애인으로 분류한
        후보입니다. 후보를 누르면 선택이 바뀝니다. 확대 후 화면을 스크롤해
        확인하세요.
      </Typography>
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: { xs: 360, sm: 500 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: '#f8fafc',
        }}
      >
        <svg
          role="group"
          aria-label="주차면 복원 후보 지도"
          viewBox={`${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`}
          style={{
            display: 'block',
            width: `${zoom * 100}%`,
            minWidth: 280,
            height: 350 * zoom,
          }}
        >
          <g stroke="#94a3b8" strokeWidth={0.06} fill="none" aria-hidden="true">
            {result.background.map((line, i) => (
              <line
                key={i}
                x1={line[0][0]}
                y1={-line[0][1]}
                x2={line[1][0]}
                y2={-line[1][1]}
              />
            ))}
          </g>
          {result.candidates.map((candidate, index) => (
            <polygon
              key={candidate.id}
              points={candidate.vertices
                .map(([x, y]) => `${x},${-y}`)
                .join(' ')}
              fill={selected.has(candidate.id) ? '#10b98188' : '#cbd5e1aa'}
              stroke={
                candidate.parking_type === 'p-parking-disable'
                  ? DISABLED_STROKE
                  : candidate.parking_type === 's-pillar'
                    ? PILLAR_STROKE
                    : selected.has(candidate.id)
                    ? '#047857'
                    : '#64748b'
              }
              strokeWidth={0.12}
              tabIndex={0}
              role="checkbox"
              aria-checked={selected.has(candidate.id)}
              aria-label={`후보 ${index + 1}, ${candidate.width} × ${
                candidate.depth
              }m${disabledLabel(candidate)}`}
              onClick={() => onToggle(candidate.id)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onToggle(candidate.id);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <title>{`후보 ${index + 1}: ${candidate.width} × ${
                candidate.depth
              }m · ${METHOD_LABEL[candidate.method]}${disabledLabel(
                candidate,
              )}`}</title>
            </polygon>
          ))}
        </svg>
      </Box>
    </Stack>
  );
};

export default RecoveryPreview;
